import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/utils/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Hospital, Mail, CheckCircle, RefreshCw } from "lucide-react";

export default function VerifyPage() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already verified
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && user.email_confirmed_at) {
        navigate("/auth/login");
      }
    };
    checkUser();
  }, [navigate]);

  const handleResendEmail = async () => {
    if (!email) {
      setMessage("Please enter your email address");
      return;
    }

    setLoading(true);
    setMessage("");
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      
      if (error) throw error;
      
      setMessage("Verification email sent! Please check your inbox.");
    } catch (err: any) {
      setMessage(err.message || "Failed to resend verification email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50">
      <section className="flex flex-1 flex-col items-center justify-center p-8">
        <Link to="/auth/signup" className="mb-8 flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors">
          <ArrowLeft className="h-5 w-5" />
          <span className="text-sm font-medium">Back to Sign Up</span>
        </Link>

        <div className="flex items-center justify-center gap-3 mb-8">
          <Hospital className="h-10 w-10 text-blue-600" />
          <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Clinic365
          </span>
        </div>

        <Card className="relative w-[420px] rounded-2xl border border-white/20 bg-white/80 shadow-2xl backdrop-blur-lg">
          <div className="absolute inset-0 -z-10 rounded-[inherit] bg-gradient-to-r from-blue-600/5 to-indigo-600/5" />
          <CardHeader className="text-center pb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Check Your Email</CardTitle>
            <p className="mt-2 text-sm text-gray-600">
              We've sent a verification link to your email address. Please click the link to verify your account.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Check your inbox for the verification email</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Click the verification link in the email</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Return here to sign in to your account</span>
              </div>
            </div>

            <div className="border-t pt-6">
              <p className="text-sm text-gray-600 mb-4">
                Didn't receive the email? Enter your email address to resend:
              </p>
              <div className="space-y-4">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Button
                  onClick={handleResendEmail}
                  disabled={loading || !email}
                  variant="outline"
                  className="w-full"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Resend Verification Email
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {message && (
              <div className={`text-center text-sm ${message.includes('sent') ? 'text-green-600' : 'text-red-500'}`}>
                {message}
              </div>
            )}

            <div className="text-center pt-4 border-t">
              <p className="text-sm text-gray-600">
                Already verified your email?{" "}
                <Link to="/auth/login" className="font-medium text-blue-600 hover:text-blue-700">
                  Sign in here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            Need help? Contact our support team at{" "}
            <a href="mailto:support@clinic365.com" className="text-blue-600 hover:text-blue-700">
              support@clinic365.com
            </a>
          </p>
        </div>
      </section>
    </div>
  );
} 